import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { analytics } from "@heycatch/sdk";
import { ChevronLeft, ChevronRight, Maximize2, Printer, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DECKS, DECK_ORDER } from "@/data/deckContent";

function Bullets({ items }) {
  return (
    <ul className="space-y-4 max-w-3xl">
      {items.map((t) => (
        <li key={t} className="flex gap-3 text-2xl text-zinc-200 leading-snug"><span className="text-[#D5A44A] mt-1">→</span>{t}</li>
      ))}
    </ul>
  );
}

function SlideBody({ s, ctx }) {
  if (s.layout === "cover") {
    return (
      <div className="relative h-full">
        <div className="absolute -inset-14 overflow-hidden pointer-events-none z-0" aria-hidden="true">
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 82% 16%, rgba(213,164,74,0.16), transparent 55%), radial-gradient(circle at 6% 94%, rgba(14,124,123,0.14), transparent 52%)" }} />
          <img src="/retainproof-icon-dark.png" alt="" className="absolute -right-20 -bottom-24 w-[460px] opacity-[0.05]" />
          <div className="absolute top-0 left-0 h-1.5 w-44 bg-[#D5A44A]" />
          <div className="absolute inset-x-0 bottom-0 h-px bg-white/10" />
        </div>
        <div className="relative z-10 h-full flex flex-col justify-center items-start">
          <img src="/retainproof-icon-dark.png" alt="RetainProof" className="h-14 w-auto mb-10" />
          {s.eyebrow && <p className="font-mono-data text-sm uppercase tracking-[0.3em] text-[#D5A44A] mb-6">{s.eyebrow}</p>}
          <h1 className="font-display font-light text-7xl tracking-tight text-white">{s.title}</h1>
          {s.subtitle && <p className="mt-8 text-3xl text-zinc-300 font-light max-w-4xl leading-snug">{s.subtitle}</p>}
          {ctx?.ref && <p className="mt-10 font-mono-data text-sm uppercase tracking-[0.25em] text-[#0E7C7B]" data-testid="deck-prepared-for">Prepared for {ctx.ref}</p>}
        </div>
      </div>
    );
  }
  if (s.layout === "cta") {
    return (
      <div className="h-full flex flex-col justify-center items-center text-center">
        {s.eyebrow && <p className="font-mono-data text-sm uppercase tracking-[0.3em] text-[#D5A44A] mb-6">{s.eyebrow}</p>}
        <h2 className="font-display font-light text-6xl tracking-tight text-white max-w-4xl">{s.title}</h2>
        {s.subtitle && <p className="mt-6 text-2xl text-zinc-300 max-w-3xl">{s.subtitle}</p>}
        {s.contact && <p className="mt-10 font-mono-data text-lg text-[#0E7C7B]">{s.contact}</p>}
      </div>
    );
  }
  return (
    <div className="h-full flex flex-col justify-center">
      {s.eyebrow && <p className="font-mono-data text-sm uppercase tracking-[0.3em] text-[#D5A44A] mb-4">{s.eyebrow}</p>}
      <h2 className="font-display font-light text-5xl tracking-tight text-white max-w-4xl leading-tight">{s.title}</h2>
      {s.subtitle && <p className="mt-6 text-2xl text-zinc-300 font-light max-w-4xl leading-snug">{s.subtitle}</p>}

      {s.bullets && <div className="mt-10"><Bullets items={s.bullets} /></div>}

      {s.stats && (
        <div className="mt-10 grid grid-cols-3 gap-6">
          {s.stats.map((st) => (
            <div key={st.l} className="border border-white/10 bg-[#12324D] p-7">
              <p className="font-display text-6xl text-[#D5A44A] leading-none">{st.v}</p>
              <p className="mt-4 text-lg text-zinc-300 leading-snug">{st.l}</p>
            </div>
          ))}
        </div>
      )}

      {s.columns && (
        <div className={`mt-10 grid gap-6 ${ { 1: "grid-cols-1", 2: "grid-cols-2", 3: "grid-cols-3", 4: "grid-cols-4" }[Math.min(s.columns.length, 4)] }`}>
          {s.columns.map((c) => (
            <div key={c.h} className="border border-white/10 bg-[#12324D] p-6">
              <p className="font-display text-2xl text-white mb-4">{c.h}</p>
              <ul className="space-y-2.5">
                {c.items.map((it) => (
                  <li key={it} className="flex gap-2.5 text-lg text-zinc-300 leading-snug"><span className="text-[#0E7C7B] mt-0.5">·</span>{it}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {s.table && (
        <div className="mt-10 border border-white/10">
          <table className="w-full text-left text-xl">
            <thead>
              <tr className="bg-white/5 font-mono-data text-sm uppercase tracking-widest text-zinc-400">
                {s.table.head.map((h) => <th key={h} className="p-5">{h}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {s.table.rows.map((r) => (
                <tr key={r[0]}>
                  {r.map((cell, i) => <td key={i} className={i === 0 ? "p-5 text-white font-medium" : "p-5 text-zinc-300 font-mono-data"}>{cell}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {s.placeholder && (
        <div className="mt-8 border-2 border-dashed border-[#D5A44A]/40 bg-[#D5A44A]/5 p-5 max-w-3xl">
          <p className="text-xl text-[#D5A44A]/90 italic">{s.placeholder}</p>
        </div>
      )}

      {s.note && <p className="mt-8 text-sm text-zinc-500 max-w-4xl leading-relaxed">{s.note}</p>}
    </div>
  );
}

function DeckSelector() {
  return (
    <div className="min-h-screen bg-[#0A1B2E] text-white font-body flex flex-col items-center justify-center px-6">
      <Helmet><title>RetainProof — Pitch Decks</title><meta name="robots" content="noindex" /></Helmet>
      <img src="/retainproof-icon-dark.png" alt="RetainProof" className="h-12 mb-8" />
      <h1 className="font-display text-4xl font-light mb-3">Pitch decks</h1>
      <p className="text-zinc-400 mb-10">Choose a deck to present or export.</p>
      <div className="grid sm:grid-cols-3 gap-6 max-w-4xl w-full">
        {DECK_ORDER.map((key) => (
          <Link key={key} to={`/deck/${key}`} data-testid={`deck-select-${key}`} className="group border border-white/10 bg-[#12324D] p-8 hover:border-[#D5A44A]/50 transition-colors">
            <p className="font-display text-2xl group-hover:text-[#D5A44A] transition-colors">{DECKS[key].label}</p>
            <p className="mt-2 text-sm text-zinc-400">{DECKS[key].audience}</p>
            <span className="mt-6 inline-flex items-center gap-1.5 text-sm text-[#D5A44A]">Open <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>
          </Link>
        ))}
      </div>
      <Link to="/" className="mt-12 text-sm text-zinc-500 hover:text-white">← Back to site</Link>
    </div>
  );
}

export default function Deck() {
  const { type } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [i, setI] = useState(0);
  const rootRef = useRef(null);
  const deck = type ? DECKS[type] : null;
  const rawRef = params.get("ref") || "";
  const ref = rawRef ? rawRef.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "";

  useEffect(() => {
    if (!type || !DECKS[type]) return;
    try { analytics.trackEvent("deck_opened", { deck: type, ref: rawRef || null }); } catch (e) { /* no-op */ }
  }, [type, rawRef]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") setI((v) => Math.min(v + 1, (deck?.slides.length || 1) - 1));
      if (e.key === "ArrowLeft") setI((v) => Math.max(v - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [deck]);

  if (!type || !deck) return <DeckSelector />;

  const total = deck.slides.length;
  const present = () => rootRef.current?.requestFullscreen?.();

  return (
    <div ref={rootRef} className="min-h-screen bg-[#0A1B2E] text-white font-body flex flex-col" data-testid="deck-page">
      <Helmet><title>{deck.seoTitle}</title><meta name="robots" content="noindex" /></Helmet>
      <style>{`
        @media screen { .deck-print { display: none; } }
        @media print {
          .deck-chrome { display: none !important; }
          .deck-screen { display: none !important; }
          .deck-print { display: block !important; }
          @page { size: 13.333in 7.5in; margin: 0; }
          .deck-page-print { width: 13.333in; height: 7.5in; page-break-after: always; overflow: hidden;
            background: #0A1B2E !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      {/* Top bar */}
      <div className="deck-chrome h-14 flex items-center justify-between px-5 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-3">
          <Link to="/deck" className="font-mono-data text-xs uppercase tracking-widest text-zinc-400 hover:text-white" data-testid="deck-all-link">All decks</Link>
          <span className="text-zinc-600">/</span>
          <span className="font-mono-data text-xs uppercase tracking-widest text-[#D5A44A]">{deck.label}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={present} className="rounded-none text-zinc-300 hover:text-white hover:bg-white/5" data-testid="deck-present-btn"><Maximize2 className="h-4 w-4 mr-2" />Present</Button>
          <Button variant="ghost" onClick={() => window.print()} className="rounded-none text-zinc-300 hover:text-white hover:bg-white/5" data-testid="deck-print-btn"><Printer className="h-4 w-4 mr-2" />Download PDF</Button>
        </div>
      </div>

      {/* On-screen single slide */}
      <div className="deck-screen flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-6xl aspect-video bg-[#0A1B2E] border border-white/10 relative overflow-hidden">
          <div className="absolute inset-0 p-14" data-testid="deck-slide">
            <SlideBody s={deck.slides[i]} ctx={{ ref }} />
          </div>
        </div>
      </div>

      {/* On-screen controls */}
      <div className="deck-chrome h-16 flex items-center justify-between px-6 border-t border-white/10 shrink-0">
        <Button variant="outline" onClick={() => setI((v) => Math.max(v - 1, 0))} disabled={i === 0} className="rounded-none border-white/20 bg-transparent" data-testid="deck-prev-btn"><ChevronLeft className="h-4 w-4 mr-1" />Prev</Button>
        <div className="flex items-center gap-2">
          {deck.slides.map((_, idx) => (
            <button key={idx} onClick={() => setI(idx)} aria-label={`Slide ${idx + 1}`} className={`h-2 w-2 rounded-full transition-colors ${idx === i ? "bg-[#D5A44A]" : "bg-white/20 hover:bg-white/40"}`} />
          ))}
          <span className="ml-3 font-mono-data text-xs text-zinc-500" data-testid="deck-counter">{i + 1} / {total}</span>
        </div>
        <Button variant="outline" onClick={() => setI((v) => Math.min(v + 1, total - 1))} disabled={i === total - 1} className="rounded-none border-white/20 bg-transparent" data-testid="deck-next-btn">Next<ChevronRight className="h-4 w-4 ml-1" /></Button>
      </div>

      {/* Print: all slides */}
      <div className="deck-print">
        {deck.slides.map((s, idx) => (
          <div key={idx} className="deck-page-print flex flex-col">
            <div className="flex-1 p-14 min-h-0"><SlideBody s={s} ctx={{ ref }} /></div>
            {idx > 0 && (
              <div className="flex items-center justify-between px-14 pb-6 font-mono-data text-[10px] uppercase tracking-widest text-zinc-500 shrink-0">
                <span>RetainProof™ · Confidential</span>
                <span>retainproofnow.com · {idx + 1} / {total}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
